import {
  AtAGlance,
  Button,
  Card,
  CardHeader,
  DashboardHero,
  GlanceRow,
  HeroCell,
  IconCalendar,
  IconMapPin,
  IconPhoneCall,
  InlineAlert,
  PageHeader,
  ProgressBar,
  RecentChecks,
  SkeletonRows,
  type CheckRowProps,
} from "@constructorfleet/ui/design-system";
import type { ScheduleResponse } from "@constructorfleet/ui";
import { useEffect, useMemo, useState, type ReactElement } from "react";
import type { CallHistoryItem } from "./components/CallHistory";
import { CollectionSites, type CollectionSite } from "./components/CollectionSites";
import { uaCoverageStats } from "./research";

type UaScheduleSnapshot = ScheduleResponse & {
  range: "today" | "tomorrow";
};

type UaDashboardData = {
  title: string;
  subtitle: string;
  today: UaScheduleSnapshot;
  tomorrow: UaScheduleSnapshot;
  updatedAt: string;
  userId?: number | null;
  callHistoryItems?: CallHistoryItem[];
  collectionSiteItems?: CollectionSite[];
  showDemoWatermark?: boolean;
  signupLink?: ReactElement;
};

function formatScheduleDate(isoDate?: string, fallback?: "today" | "tomorrow") {
  if (!isoDate) return fallback === "tomorrow" ? "Tomorrow" : "Today";
  const parsed = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return isoDate;
  return new Intl.DateTimeFormat(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  }).format(parsed);
}

function formatTimestamp(value: string) {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatTime(value: string, timeZone?: string) {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone,
  }).format(parsed);
}

function summarizeSchedule(snapshot: UaScheduleSnapshot) {
  if (snapshot.items.length === 0) {
    return snapshot.range === "today" ? "No reporting window yet" : "No scheduled item yet";
  }
  return snapshot.items[0]?.title ?? "Scheduled";
}

function buildPrimaryCallout(today: UaScheduleSnapshot, tomorrow: UaScheduleSnapshot) {
  const todayItem = today.items[0];
  if (todayItem) {
    return {
      tone: "urgent" as const,
      kicker: "Today's required action",
      statusLabel: todayItem.title,
      headline: todayItem.status
        ? `${todayItem.status}${todayItem.location ? ` at ${todayItem.location}` : ""}.`
        : `A same-day UA instruction is scheduled${todayItem.location ? ` at ${todayItem.location}` : ""}.`,
      footer: `Today · ${formatScheduleDate(today.isoDate, "today")}`,
    };
  }

  const tomorrowItem = tomorrow.items[0];
  if (tomorrowItem) {
    return {
      tone: "watch" as const,
      kicker: "Tomorrow's schedule",
      statusLabel: tomorrowItem.title,
      headline: `No same-day instruction is logged right now. Tomorrow's next check starts at ${formatTime(
        tomorrowItem.start_time,
        tomorrow.timezone
      )}${tomorrowItem.location ? ` at ${tomorrowItem.location}` : ""}.`,
      footer: `Tomorrow · ${formatScheduleDate(tomorrow.isoDate, "tomorrow")}`,
    };
  }

  return {
    tone: "neutral" as const,
    kicker: "No selection yet",
    statusLabel: "Waiting for the next automated check",
    headline:
      "The dashboard will update as soon as the next UA line check logs a result or reporting window.",
    footer: "Live status stays visible here.",
  };
}

async function fetchJson<TValue>(url: string): Promise<TValue> {
  const response = await fetch(url, { credentials: "same-origin" });
  if (!response.ok) {
    throw new Error(`Request failed: ${response.status}`);
  }
  return (await response.json()) as TValue;
}

function heroStateFromTone(
  tone: "urgent" | "watch" | "neutral"
): "report" | "scheduled" | undefined {
  if (tone === "urgent") return "report";
  if (tone === "watch") return "scheduled";
  return undefined;
}

function buildCheckRow(item: CallHistoryItem): CheckRowProps {
  const resultLower = (item.result_label ?? "").toLowerCase();
  const isReport = resultLower.includes("report");
  const isNoTesting = resultLower.includes("no testing");

  const rail: CheckRowProps["rail"] = isNoTesting ? "ok" : isReport ? "rep" : "default";
  const pill: CheckRowProps["pill"] = isReport
    ? { label: "REPORT", tone: "rep" }
    : isNoTesting
      ? { label: "OK", tone: "default" }
      : undefined;

  const parsed = new Date(item.timestamp);
  const validDate = !Number.isNaN(parsed.getTime());

  const timePrimary = validDate
    ? new Intl.DateTimeFormat(undefined, {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      }).format(parsed)
    : item.timestamp;

  const timeSecondary = validDate
    ? new Intl.DateTimeFormat(undefined, {
        month: "short",
        day: "numeric",
      }).format(parsed)
    : undefined;

  return {
    rail,
    title: item.result_label ?? "Completed",
    pill,
    snippet: item.transcript_snippet,
    when: { primary: timePrimary, secondary: timeSecondary },
    href: item.audio_signed_url ?? undefined,
  };
}

function UaRecentCallHistory({
  userId,
  staticItems,
}: {
  userId?: number | null;
  staticItems?: CallHistoryItem[];
}) {
  const [loading, setLoading] = useState(staticItems == null);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<CallHistoryItem[]>(staticItems ?? []);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(staticItems ? 1 : 1);

  useEffect(() => {
    if (staticItems) {
      setItems(staticItems);
      setLoading(false);
      setError(null);
      setTotalPages(1);
      return;
    }
    if (typeof userId !== "number") {
      setItems([]);
      setLoading(false);
      setError("Call history is unavailable.");
      return;
    }

    let active = true;
    setLoading(true);
    setError(null);

    void fetchJson<{
      ok?: boolean;
      error?: string;
      total?: number;
      items?: CallHistoryItem[];
    }>(`/api/website/users/${userId}/call-history?page=${page}&pageSize=${pageSize}`)
      .then((result) => {
        if (!active) return;
        if (!result.ok) {
          setError(result.error ?? "Failed to load call history");
          setItems([]);
          setTotalPages(1);
          return;
        }
        setItems(result.items ?? []);
        const total = Number(result.total ?? 0) || 0;
        setTotalPages(Math.max(1, Math.ceil(total / pageSize)));
      })
      .catch(() => {
        if (!active) return;
        setError("Failed to load call history");
        setItems([]);
        setTotalPages(1);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [page, pageSize, staticItems, userId]);

  const rows: CheckRowProps[] = useMemo(() => items.map(buildCheckRow), [items]);

  return (
    <>
      {loading ? (
        <SkeletonRows count={4} />
      ) : error ? (
        <InlineAlert tone="danger">{error}</InlineAlert>
      ) : rows.length === 0 ? (
        <InlineAlert tone="info">No calls found.</InlineAlert>
      ) : (
        <RecentChecks
          title="Call history"
          meta="Review completed calls, confirm the spoken result, and open audio or transcript details."
          rows={rows}
        />
      )}

      {!loading && !error && !staticItems ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "var(--cf-space-sm)",
            marginTop: "var(--cf-space-12)",
          }}
        >
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={page <= 1}
            onClick={() => setPage((current) => current - 1)}
          >
            Previous
          </Button>
          <span className="hc-desc">
            Page {page} of {totalPages}
          </span>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={page >= totalPages}
            onClick={() => setPage((current) => current + 1)}
          >
            Next
          </Button>
        </div>
      ) : null}
    </>
  );
}

function UaDashboardChrome({
  data,
  loadError,
}: {
  data: UaDashboardData;
  loadError?: string | null;
}): ReactElement {
  const callout = buildPrimaryCallout(data.today, data.tomorrow);
  const recentHistoryValue = data.callHistoryItems ? String(data.callHistoryItems.length) : "0";
  const todayItem = data.today.items[0];
  const tomorrowItem = data.tomorrow.items[0];

  const checkRows: CheckRowProps[] = (data.callHistoryItems ?? []).map(buildCheckRow);

  return (
    <div className="page">
      <PageHeader
        title={data.title}
        sub={data.subtitle}
        meta={{
          label: "Today",
          value: new Date().toLocaleDateString(undefined, {
            weekday: "short",
            month: "short",
            day: "numeric",
            year: "numeric",
          }),
          subValue: `${new Date().toLocaleTimeString(undefined, {
            hour: "numeric",
            minute: "2-digit",
          })} · ${data.today.timezone ?? "America/Denver"}`,
        }}
      />

      {loadError ? <InlineAlert tone="info">{loadError}</InlineAlert> : null}

      <DashboardHero state={heroStateFromTone(callout.tone)} cols={3}>
        <HeroCell
          kickerLabel={callout.kicker}
          big={callout.statusLabel}
          bigTone={
            callout.tone === "urgent" ? "danger" : callout.tone === "watch" ? "default" : "success"
          }
          desc={callout.headline}
          cell="result"
        />
        <HeroCell
          kickerIcon={<IconCalendar style={{ width: 14, height: 14 }} />}
          kickerLabel="Next report window"
          big={
            tomorrowItem ? formatScheduleDate(data.tomorrow.isoDate, "tomorrow") : "Not scheduled"
          }
          desc={
            tomorrowItem
              ? `Window opens ${formatTime(tomorrowItem.start_time, data.tomorrow.timezone)}${tomorrowItem.location ? ` at ${tomorrowItem.location}` : ""}. We'll text a reminder before.`
              : "The next reporting window will appear after the automated line check completes."
          }
          cell="next"
        />
        <HeroCell
          kickerIcon={<IconPhoneCall style={{ width: 14, height: 14 }} />}
          kickerLabel="Check-in line"
          big={todayItem ? "Healthy" : "Waiting"}
          desc={
            todayItem
              ? `Line answered successfully. Automated menu matched on the first try.`
              : "Line status will update after the next automated check completes."
          }
          cell="line"
        />
      </DashboardHero>

      <div className="grid">
        <Card>
          <CardHeader title="Quick reassurance" />
          <div className="juris">
            <div className="ds-stack">
              <div>
                <strong>Current status</strong>
                <p className="hc-desc">{summarizeSchedule(data.today)}</p>
              </div>
              <div>
                <strong>Next line check</strong>
                <p className="hc-desc">
                  {tomorrowItem
                    ? `${formatTime(tomorrowItem.start_time, data.tomorrow.timezone)} tomorrow`
                    : "The next completed line check will populate tomorrow's status."}
                </p>
              </div>
            </div>
            <div className="stats">
              <div>
                <div className="lbl">Today</div>
                <div className="val">{summarizeSchedule(data.today)}</div>
              </div>
              <div>
                <div className="lbl">Tomorrow</div>
                <div className="val">{summarizeSchedule(data.tomorrow)}</div>
              </div>
              <div>
                <div className="lbl">Checks logged</div>
                <div className="val">{recentHistoryValue}</div>
              </div>
              <div>
                <div className="lbl">Last refreshed</div>
                <div className="val">{formatTimestamp(data.updatedAt)}</div>
              </div>
            </div>
          </div>
        </Card>

        <AtAGlance title="At a glance" meta={`updated ${formatTimestamp(data.updatedAt)}`}>
          <GlanceRow
            icon={<IconCalendar />}
            iconTone="mute"
            title="Tomorrow's check"
            subtitle={
              tomorrowItem
                ? `Line will be called at ${formatTime(tomorrowItem.start_time, data.tomorrow.timezone)}`
                : "Schedule pending"
            }
            value={formatScheduleDate(data.tomorrow.isoDate, "tomorrow")}
          />
          <GlanceRow
            icon={<IconPhoneCall />}
            iconTone="brand"
            title="Recent call history"
            value={recentHistoryValue}
            subtitle={`Last refreshed ${formatTimestamp(data.updatedAt)}`}
          />
          <GlanceRow
            icon={<IconMapPin />}
            title="Coverage footprint"
            value={`${uaCoverageStats.testingLocationCount} sites`}
            subtitle={`${uaCoverageStats.monitoredProgramCount} monitored programs`}
          />
        </AtAGlance>
      </div>

      <UaRecentCallHistory userId={data.userId} staticItems={data.callHistoryItems} />
      <CollectionSites items={data.collectionSiteItems} />

      {data.showDemoWatermark ? (
        <div className="demo-watermark">
          <span className="demo-watermark-label">Demo</span>
          <span className="demo-watermark-copy">This is sample data.</span>
          {data.signupLink}
        </div>
      ) : null}
    </div>
  );
}

function emptySnapshot(range: "today" | "tomorrow"): UaScheduleSnapshot {
  return {
    range,
    items: [],
    timezone: "America/Denver",
  };
}

function emptyDashboardData(userId?: number | null): UaDashboardData {
  return {
    title: "UA Notifications Dashboard",
    subtitle:
      "BHG-style visibility for today's result, tomorrow's schedule, recent call history, and nearby collection sites.",
    today: emptySnapshot("today"),
    tomorrow: emptySnapshot("tomorrow"),
    updatedAt: new Date().toISOString(),
    userId,
  };
}

function useUaDashboardData() {
  const [userId, setUserId] = useState<number | null>(null);
  const [data, setData] = useState<UaDashboardData | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [sessionLoaded, setSessionLoaded] = useState(false);

  useEffect(() => {
    let active = true;

    void fetchJson<{ user?: { id?: number | string } }>("/api/auth/session")
      .then((result) => {
        if (!active) return;
        const rawId = result.user?.id;
        const parsed = Number(rawId);
        setUserId(Number.isFinite(parsed) ? parsed : null);
        setSessionLoaded(true);
      })
      .catch(() => {
        if (active) {
          setUserId(null);
          setSessionLoaded(true);
        }
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!sessionLoaded) return;

    if (userId === null) {
      setLoadError("Sign in to load your UA dashboard data.");
      setData(emptyDashboardData(null));
      return;
    }

    let active = true;
    setLoadError(null);

    void Promise.all([
      fetchJson<ScheduleResponse>(`/api/website/users/${userId}/schedule?range=today`),
      fetchJson<ScheduleResponse>(`/api/website/users/${userId}/schedule?range=tomorrow`),
    ])
      .then(([today, tomorrow]) => {
        if (!active) return;
        setData({
          title: "UA Notifications Dashboard",
          subtitle:
            "BHG-style visibility for today's result, tomorrow's schedule, recent call history, and nearby collection sites.",
          today: { ...today, range: "today" },
          tomorrow: { ...tomorrow, range: "tomorrow" },
          updatedAt: new Date().toISOString(),
          userId,
        });
      })
      .catch(() => {
        if (!active) return;
        setLoadError(
          "Some UA dashboard data could not be loaded. Showing the latest available dashboard state."
        );
        setData(emptyDashboardData(userId));
      });

    return () => {
      active = false;
    };
  }, [sessionLoaded, userId]);

  return { userId, data, loadError, sessionLoaded };
}

function buildDemoData(): UaDashboardData {
  return {
    title: "UA Demo Dashboard",
    subtitle:
      "Sample UA notification history with the same today-first emphasis, tomorrow visibility, and call-history layout as the live dashboard.",
    today: {
      range: "today",
      timezone: "America/Denver",
      isoDate: "2026-04-05",
      items: [
        {
          id: "demo-today-1",
          start_time: "2026-04-05T08:00:00.000-06:00",
          title: "Selected today for observed UA test",
          location: "Jefferson County collection site",
          status: "report before 5:00 PM",
        },
      ],
    },
    tomorrow: {
      range: "tomorrow",
      timezone: "America/Denver",
      isoDate: "2026-04-06",
      items: [
        {
          id: "demo-tomorrow-1",
          start_time: "2026-04-06T07:00:00.000-06:00",
          title: "Tomorrow line opens",
          location: "Automated probation line",
          status: "check after 7:00 AM",
        },
      ],
    },
    callHistoryItems: [
      {
        call_id: "UA-DEMO-006",
        timestamp: "2026-04-05T06:10:00.000-06:00",
        other_party: "Colorado UA line",
        result_label: "Report today — observed UA test",
        result_date: "2026-04-05",
        transcript_snippet:
          "Automated line confirmed color blue and instructed same-day reporting for an observed UA test.",
        transcript_full_url: "#demo-transcript-6",
        audio_signed_url: "#demo-audio-6",
      },
      {
        call_id: "UA-DEMO-005",
        timestamp: "2026-04-04T06:12:00.000-06:00",
        other_party: "Colorado UA line",
        result_label: "No testing today",
        result_date: "2026-04-04",
        transcript_snippet:
          "Automated line reported no same-day testing requirement and reminded the participant to call again tomorrow.",
        transcript_full_url: "#demo-transcript-5",
        audio_signed_url: "#demo-audio-5",
      },
      {
        call_id: "UA-DEMO-004",
        timestamp: "2026-04-03T06:08:00.000-06:00",
        other_party: "Colorado UA line",
        result_label: "No testing today",
        result_date: "2026-04-03",
        transcript_snippet:
          "Automated line reported no same-day testing requirement. No action needed today.",
        transcript_full_url: "#demo-transcript-4",
        audio_signed_url: "#demo-audio-4",
      },
      {
        call_id: "UA-DEMO-003",
        timestamp: "2026-04-02T06:15:00.000-06:00",
        other_party: "Colorado UA line",
        result_label: "Report today — random UA screen",
        result_date: "2026-04-02",
        transcript_snippet:
          "Automated line confirmed color blue and instructed same-day reporting for a random UA screen.",
        transcript_full_url: "#demo-transcript-3",
        audio_signed_url: "#demo-audio-3",
      },
      {
        call_id: "UA-DEMO-002",
        timestamp: "2026-04-01T06:11:00.000-06:00",
        other_party: "Colorado UA line",
        result_label: "No testing today",
        result_date: "2026-04-01",
        transcript_snippet:
          "Automated line reported no same-day testing requirement. Participant cleared for the day.",
        transcript_full_url: "#demo-transcript-2",
        audio_signed_url: "#demo-audio-2",
      },
      {
        call_id: "UA-DEMO-001",
        timestamp: "2026-03-31T06:09:00.000-06:00",
        other_party: "Colorado UA line",
        result_label: "No testing today",
        result_date: "2026-03-31",
        transcript_snippet:
          "Automated line reported no same-day testing requirement. No action needed today.",
        transcript_full_url: "#demo-transcript-1",
        audio_signed_url: "#demo-audio-1",
      },
      {
        call_id: "UA-DEMO-000",
        timestamp: "2026-03-30T06:14:00.000-06:00",
        other_party: "Colorado UA line",
        result_label: "Report today — observed UA test",
        result_date: "2026-03-30",
        transcript_snippet:
          "Automated line confirmed color blue and instructed same-day reporting for an observed UA test.",
        transcript_full_url: "#demo-transcript-0",
        audio_signed_url: "#demo-audio-0",
      },
    ],
    collectionSiteItems: [
      {
        id: "demo-site-1",
        name: "Jefferson County Intake Center",
        address: "123 Main St, Lakewood, CO",
        lat: 39.7447,
        lng: -105.0858,
        opening_hours: ["Monday-Friday: 6:00 AM - 5:30 PM", "Saturday: 7:00 AM - 12:00 PM"],
        approved: true,
        open_now: true,
      },
      {
        id: "demo-site-2",
        name: "Denver Downtown Testing Lab",
        address: "210 Broadway, Denver, CO",
        lat: 39.7239,
        lng: -104.9888,
        opening_hours: ["Monday-Friday: 7:00 AM - 6:00 PM"],
        approved: true,
        open_now: false,
      },
    ],
    updatedAt: "2026-04-05T06:15:00.000-06:00",
    showDemoWatermark: true,
  };
}

export function UaLiveDashboard(): ReactElement {
  const { userId, data, loadError, sessionLoaded } = useUaDashboardData();

  if (!sessionLoaded || data === null) {
    return (
      <div className="page ds-stack">
        <PageHeader title="UA Line Check" sub="Loading your dashboard." />
        <ProgressBar label="Loading dashboard" />
      </div>
    );
  }

  return <UaDashboardChrome data={data} loadError={loadError} />;
}

export function UaDemoDashboard({ signupLink }: { signupLink?: ReactElement } = {}): ReactElement {
  return <UaDashboardChrome data={{ ...buildDemoData(), signupLink }} />;
}
